import React from "react";
import { Link } from "react-router-dom";
import { Wrench, Heart, ArrowRight, ArrowLeft, Eye } from "lucide-react";
import { Editable } from "./Editable";
import NewsletterForm from "./NewsletterForm";

/**
 * Maintenance / Under-construction screen.
 * - variant="maintenance" → reads maintenance.* content keys, used by App.js as site-wide override
 * - variant="coming_soon" → reads coming_soon.* content keys, used by /coming-soon route
 *
 * adminPreview=true → renders a banner reminding admins they're seeing what visitors see,
 * with a quick link back to /admin. Inline `<Editable>` fields still let them edit copy.
 */
const MaintenancePage = ({ variant = "maintenance", adminPreview = false }) => {
    const isMaint = variant === "maintenance";
    const prefix = isMaint ? "maintenance" : "coming_soon";
    return (
        <div
            data-testid={`${prefix}-page`}
            className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center px-6 py-20"
        >
            {adminPreview && (
                <div
                    data-testid="maintenance-admin-banner"
                    className="fixed top-0 inset-x-0 z-40 bg-[#1C252E] text-white text-xs sm:text-sm py-2.5 px-4 flex items-center justify-center gap-3 flex-wrap"
                >
                    <Eye className="w-4 h-4 text-amber-300" strokeWidth={2} />
                    <span>
                        <strong className="font-semibold">Admin preview</strong> — maintenance mode is ON. Click any heading or paragraph below to edit it inline.
                    </span>
                    <Link
                        to="/admin"
                        data-testid="maintenance-admin-back"
                        className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 px-3 py-1 rounded-full font-medium transition-colors"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back to admin
                    </Link>
                </div>
            )}
            <div className="max-w-2xl w-full text-center">
                <div className="w-16 h-16 rounded-full bg-[#F2D8D5] flex items-center justify-center mx-auto mb-8">
                    {isMaint ? (
                        <Wrench className="w-7 h-7 text-[#E53935]" strokeWidth={1.5} />
                    ) : (
                        <Heart className="w-7 h-7 text-[#E53935]" fill="#E53935" strokeWidth={1.5} />
                    )}
                </div>
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-4">
                    {isMaint ? "Site maintenance" : "Coming soon"}
                </p>
                <Editable
                    contentKey={`${prefix}.headline`}
                    as="h1"
                    className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-6"
                >
                    {isMaint ? "We'll be right back" : "Something great is coming"}
                </Editable>
                <Editable
                    contentKey={`${prefix}.body`}
                    as="p"
                    multiline
                    className="text-lg text-[#4A5568] leading-relaxed mb-8"
                >
                    {isMaint
                        ? "We're polishing a few things behind the scenes."
                        : "Sign up below to be the first to know when this is live."}
                </Editable>
                {isMaint && (
                    <div className="inline-block bg-[#F4F1EA] border border-black/5 rounded-full px-5 py-2 mb-10">
                        <Editable contentKey="maintenance.eta" as="span" className="text-sm font-medium text-[#1C252E]">
                            Back online within the hour
                        </Editable>
                    </div>
                )}
                <div className="max-w-md mx-auto">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#1C252E] mb-3">
                        Stay in the loop
                    </p>
                    <NewsletterForm source={prefix} variant="light" />
                </div>
                {!isMaint && (
                    <Link
                        to="/"
                        data-testid="coming-soon-home-link"
                        className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-[#1C252E] hover:text-[#E53935] transition-colors"
                    >
                        Back to home
                        <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                )}
            </div>
        </div>
    );
};

export default MaintenancePage;
